import { apiRequests } from '../utilities/axios-factory'
import store from '../store'
import { getFromCache, setToCache } from './cache-service'

function getAgent ({ id, include, includeArchived }) {
  include = include || [{ stUser: 'roles' }, 'countryCurrency', 'agentMobileMoneyAccounts', 'agentProfileDocuments']

  return apiRequests.get(`/v1/${includeArchived ? 'raw-' : ''}agent-profile/` + id + '/join/', {
    params: { filter: { include } },
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

/**
 * @param {object} options
 * @param {string} options.documentId
 * @returns {Promise<string>}
 */
function getAgentSecureUrl ({ documentId }) {
  return apiRequests.get(`/v1/agent-profile-document/${documentId}/secure-url/`, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data && result.data.url
  })
}

function getAgentPhotoSecureUrl ({ id }) {
  return apiRequests.get(`/v1/agent-profile/${id}/photo/secure-url/`, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data && result.data.url
  })
}

function verifyAgentInvitation ({ verificationToken, password, confirmedPassword }) {
  return apiRequests.put('/v1/st-user/invitation/verification', { verificationToken, password, confirmedPassword }).then((result) => {
    return result.data
  })
}

function updateAgent ({
  id,
  firstName,
  lastName,
  idDocumentExpiryDate,
  idDocumentFrontFile,
  idDocumentBackFile
}) {
  const bodyFormData = new FormData()

  if (firstName) {
    bodyFormData.append('firstName', firstName)
  }

  if (lastName) {
    bodyFormData.append('lastName', lastName)
  }

  if (idDocumentExpiryDate) {
    bodyFormData.append('idDocumentExpiryDate', idDocumentExpiryDate)
  }

  if (idDocumentFrontFile) {
    bodyFormData.append('idDocumentFront', idDocumentFrontFile)
  }

  if (idDocumentBackFile) {
    bodyFormData.append('idDocumentBack', idDocumentBackFile)
  }

  return apiRequests.patch(`/v1/agent-profile/${id}/`, bodyFormData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: store.getters.tokenId
    }
  }).then((result) => {
    return result.data
  })
}

function updateAgentSensitiveData ({
  id,
  email,
  phoneNumber,
  recoveryContact,
  password
}) {
  return apiRequests.patch(`/v1/agent-profile/${id}/sensitive-data/`, {
    email,
    phoneNumber,
    recoveryContact,
    password
  }, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

function sendAgentPasswordChangeRequest ({ email, phone }) {
  return apiRequests.put('/v1/agent-profile/password-reset-request/', {
    email,
    phone
  }).then((result) => {
    return result.data
  })
}

function sendPassengerCredit ({ agentProfileId, passengerProfileId, amount }) {
  return apiRequests.post(`/v1/agent-profile/${agentProfileId}/send-passenger-credit/`, {
    passengerProfileId,
    amount
  }, {
    headers: {
      Authorization: store.getters.tokenId,
      'x-agent-authorization': store.getters.passengerAuthorizationToken && store.getters.passengerAuthorizationToken.tokenId
    }
  }).then((result) => {
    return result.data
  })
}

function hasRecentPassengerTopup ({ agentProfileId, phoneNumber }) {
  return apiRequests.get(`/v1/agent-profile/${agentProfileId}/recent-passenger-topup/`, {
    params: { phoneNumber },
    headers: {
      Authorization: store.getters.tokenId,
      'x-agent-authorization': store.getters.passengerAuthorizationToken && store.getters.passengerAuthorizationToken.tokenId
    }
  }).then((result) => {
    return result.data
  })
}

async function getConversionRatePercentSetting (countryId) {
  return apiRequests.get(`/v1/st-setting/agent-conversion-rate/${countryId}/`, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

async function getCachedConversionRatePercent (agentProfile) {
  let conversionRatePercent

  if (agentProfile.customConversionRatePercent) {
    conversionRatePercent = agentProfile.customConversionRatePercent
  } else {
    const result = getFromCache('conversionRatePercent')

    if (result?.conversionRatePercent) {
      conversionRatePercent = result.conversionRatePercent
    } else {
      const stSetting = await getConversionRatePercentSetting(agentProfile.countryId)
      conversionRatePercent = stSetting.conversionRatePercent
      setToCache({ key: 'conversionRatePercent', value: { conversionRatePercent }, expiryInSeconds: 30 * 60 })
    }
  }

  return conversionRatePercent
}

function sendAgentGift ({ agentProfileId, toPhoneNumber, toEmail, amount }) {
  return apiRequests.post(`/v1/agent-profile/${agentProfileId}/agent-gift/`, {
    toPhoneNumber,
    toEmail,
    amount
  }, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

function getAgentGifts ({ agentProfileId, pendingOnly }) {
  return apiRequests.get(`/v1/agent-profile/${agentProfileId}/agent-gifts/`, {
    params: pendingOnly ? { pendingOnly: true } : {},
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

function acceptAgentGift ({ agentGiftId }) {
  return apiRequests.patch(`/v1/agent-gift/${agentGiftId}/accept/`, {}, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

function cancelAgentGift ({ agentGiftId }) {
  return apiRequests.delete(`/v1/agent-gift/${agentGiftId}/`, {
    headers: { Authorization: store.getters.tokenId }
  }).then((result) => {
    return result.data
  })
}

export {
  getAgent,
  getAgentSecureUrl,
  getAgentPhotoSecureUrl,
  verifyAgentInvitation,
  updateAgent,
  updateAgentSensitiveData,
  sendAgentPasswordChangeRequest,
  sendPassengerCredit,
  hasRecentPassengerTopup,
  getConversionRatePercentSetting,
  getCachedConversionRatePercent,
  sendAgentGift,
  getAgentGifts,
  acceptAgentGift,
  cancelAgentGift
}
